'use client';

import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '@mokoboko/components';
import {
  Modal,
  Input,
  Link,
  Button,
  Avatar,
  StatusIcon,
} from '@mokoboko/components';

import { PatternModalHeader } from '@/components/dialogs/pattern-modal-header';
import { requestPasswordReset } from '@/services/auth';
import { GENERIC_ERROR_MESSAGE } from '@/lib/constants';
import { getErrorMessage } from '@/lib/error-utils';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import useTenant from '@/state/tenant';

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: VoidFunction;
  onLoginLinkClick: VoidFunction;
  defaultEmail?: string;
}

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'Please enter your email' })
    .email({ message: 'Invalid email address' }),
});

type FormValues = z.infer<typeof schema>;

export const ResetPasswordDialog = ({
  open,
  onClose,
  onLoginLinkClick,
  defaultEmail,
}: ResetPasswordDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: defaultEmail ?? '' },
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [shake, setShake] = useState(0);
  const { data: tenant } = useTenant();

  const logoUrl = tenant?.logo || '/images/logo.png';

  const onSubmit = async (data: FormValues) => {
    // The shared Button stays enabled while it spins, so a second Enter still
    // submits the form. A second request would replace the first email's link.
    if (isProcessing) return;
    try {
      setIsProcessing(true);
      setFormError('');

      const response = await requestPasswordReset({
        email: data.email?.toLowerCase(),
      });
      if (response?.message) {
        toast.success(response.message);
      }
      setIsSubmitted(true);
      reset();
      track({ event: AnalyticsEvent.PASSWORD_RESET_REQUESTED });
    } catch (error) {
      setFormError(getErrorMessage(error, GENERIC_ERROR_MESSAGE));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Content size="sm" hasCloseButton isPreventClose={isProcessing}>
        <PatternModalHeader>
          {isSubmitted ? (
            <StatusIcon variant="success" size="xl" className="relative !h-16 !w-16 [&_svg]:!h-6 [&_svg]:!w-6" />
          ) : (
            <Avatar
              src={logoUrl}
              name={tenant?.name || 'MB'}
              size="xl"
              className="relative bg-pine-600 text-white"
            />
          )}

          <Modal.Title className="relative mt-4 text-gray-900">
            {isSubmitted ? 'Email sent' : 'Reset password'}
          </Modal.Title>
          <Modal.Description className="relative mt-1 max-w-sm text-gray-700">
            {isSubmitted
              ? "Check your inbox for password reset instructions. If you didn't find the email, check your Spam folder"
              : "Enter the email address associated with your account. We'll send you password reset steps."}
          </Modal.Description>
        </PatternModalHeader>

        <Modal.Body className="flex flex-col gap-6 overflow-visible p-5 pt-0">
          {isSubmitted ? (
            <Button
              fullWidth
              onClick={onClose}
              size="lg"
              variant="secondary-gray"
            >
              Ok
            </Button>
          ) : (
            <form
              className="space-y-5"
              onSubmit={handleSubmit(onSubmit, () => setShake((s) => s + 1))}
            >
              {formError && (
                <p className="text-sm leading-5 text-error-500">{formError}</p>
              )}

              <Input
                {...register('email')}
                hasError={!!errors?.email}
                errorMessage={errors?.email?.message}
                shake={errors?.email ? shake : 0}
                placeholder="Email"
              />

              <Button
                fullWidth
                isProcessing={isProcessing}
                type="submit"
                size="lg"
              >
                {isProcessing ? 'Sending reset email…' : 'Reset password'}
              </Button>

              <div className="border-t border-gray-200" />

              <div className="text-center">
                <Link
                  color="red"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    onLoginLinkClick();
                  }}
                  role="button"
                  tabIndex={0}
                >
                  Found your password? Login
                </Link>
              </div>
            </form>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
